// ReportTypesContext.js
"use client";
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { BASE_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const ReportTypesContext = createContext();
export const useReportTypes = () => useContext(ReportTypesContext);

// 🔁 Normalize backend data
const normalizeReportType = (r) => ({
  id: r?.id ? String(r.id) : "",
  title: r?.title ?? "",
  description: r?.description ?? "",
  status: r?.status ? r.status.toLowerCase() : "inactive",
  create_audit_id: r?.create_audit_id ?? null,
  create_audit_time: r?.create_audit_time ?? null,
  update_audit_id: r?.update_audit_id ?? null,
  update_audit_time: r?.update_audit_time ?? null,
  delete_audit_id: r?.delete_audit_id ?? null,
  delete_audit_time: r?.delete_audit_time ?? null,
});

export const ReportTypesProvider = ({ children }) => {
  const { token } = useAuth();

  const [reportTypes, setReportTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const ENDPOINT = `${BASE_URL}/report-types`;

  // 🔹 FETCH
  const fetchReportTypes = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(ENDPOINT, { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Failed to fetch report types");
      const json = await res.json();
      const list = Array.isArray(json.data) ? json.data : json;
      setReportTypes(list.map(normalizeReportType));
    } catch (err) {
      setReportTypes([]);
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔹 ADD
  const addReportType = async (payload) => {
    const create_audit_id = localStorage.getItem("admin_id");
    const res = await fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        title: payload.title,
        description: payload.description,
        status: payload.status === "active" ? "Active" : "Inactive",
        create_audit_id,
      }),
    });
    const json = await res.json();
    const created = normalizeReportType(json.data ?? json);
    setReportTypes((prev) => [created, ...prev]);
  };

  // 🔹 UPDATE
  const updateReportType = async (payload) => {
    const update_audit_id = localStorage.getItem("admin_id");
    const res = await fetch(`${ENDPOINT}/${payload.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        title: payload.title,
        description: payload.description,
        status: payload.status === "active" ? "Active" : "Inactive",
        update_audit_id,
      }),
    });
    const json = await res.json();
    const updated = normalizeReportType(json.data ?? json);
    setReportTypes((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
  };

  // 🔹 DELETE
  const deleteReportType = async (id) => {
    const delete_audit_id = localStorage.getItem("admin_id");
    await fetch(`${ENDPOINT}/${id}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ delete_audit_id }),
    });
    setReportTypes((prev) => prev.filter((r) => r.id !== String(id)));
  };

  // 🔹 TOGGLE STATUS
  const toggleReportStatus = async (item) => {
    await updateReportType({
      ...item,
      status: item.status === "active" ? "inactive" : "active",
    });
  };

  useEffect(() => {
    fetchReportTypes();
  }, [fetchReportTypes]);

  return (
    <ReportTypesContext.Provider
      value={{
        reportTypes,
        loading,
        error,
        addReportType,
        updateReportType,
        deleteReportType,
        toggleReportStatus,
        refreshReportTypes: fetchReportTypes,
      }}
    >
      {children}
    </ReportTypesContext.Provider>
  );
};
